import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { usersApi, companiesApi, accountsApi, profilesApi } from "../services/apiClient";
import { getRoleFromUser } from "../constants/roles";
import { setupVinculacion } from "../services/vinculacion";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const STORAGE_KEYS = {
  USER_ID: "crm_user_id",
  COMPANY_ID: "crm_active_company_id",
  ACCOUNT_ID: "crm_active_account_id",
  PROFILE_ID: "crm_active_profile_id",
  PROFILE_TYPE: "crm_active_profile_type",
  SESSION_TIMESTAMP: "crm_session_timestamp",
  JWT_TOKEN: "jwt_token",
};

const jwtDecode = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const decoded = JSON.parse(jsonPayload);
    return decoded.data;
  } catch (e) {
    return null;
  }
};

const getAuthDataFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    userId: params.get("user_id") || params.get("userId"),
    token: params.get("token") || params.get("jwt"),
    profileId: params.get("profile_id"),
    profileType: params.get("profile_type")
  };
};

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;
const REFRESH_INTERVAL_MS = 30 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [activeCompany, setActiveCompany] = useState(null);
  const [activeAccount, setActiveAccount] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);

  const [isContextLoading, setIsContextLoading] = useState(false);

  const hasInitialized = useRef(false);

  // ---------------- STORAGE ----------------
  const saveToStorage = useCallback((userId, companyId = null, accountId = null, profileId = null, profileType = null) => {
    if (userId) localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
    if (companyId) localStorage.setItem(STORAGE_KEYS.COMPANY_ID, companyId);
    if (accountId) localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, accountId);
    if (profileId) localStorage.setItem(STORAGE_KEYS.PROFILE_ID, profileId);
    if (profileType) localStorage.setItem(STORAGE_KEYS.PROFILE_TYPE, profileType);
    localStorage.setItem(STORAGE_KEYS.SESSION_TIMESTAMP, Date.now().toString());
  }, []);

  const clearStorage = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  }, []);

  const getStoredContext = useCallback(() => ({
    userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
    companyId: localStorage.getItem(STORAGE_KEYS.COMPANY_ID),
    accountId: localStorage.getItem(STORAGE_KEYS.ACCOUNT_ID),
    profileId: localStorage.getItem(STORAGE_KEYS.PROFILE_ID),
    sessionTimestamp: localStorage.getItem(STORAGE_KEYS.SESSION_TIMESTAMP),
  }), []);

  const isSessionExpired = useCallback(() => {
    const stored = getStoredContext();
    if (!stored.sessionTimestamp) {
      return true;
    }
    const elapsed = Date.now() - parseInt(stored.sessionTimestamp, 10);
    return elapsed > SESSION_DURATION_MS;
  }, [getStoredContext]);

  // ---------------- USER ----------------
  const loadUserData = useCallback(async (userId) => {
    try {
      const response = await usersApi.get({ user_id: userId });

      if (response.success && response.item) {
        const u = response.item;
        const mappedUser = {
          id: u.id || userId,
          external_user_id: u.external_user_id || userId,
          email: u.email || u.correo,
          nombre: u.nombre,
          apellido: u.apellido,
          avatar: u.avatar,
          role: getRoleFromUser(u),
          telefono: u.telefono,
          estado: u.estado_usuario,
        };

        setUser(mappedUser);
        setIsAuthenticated(true);
        saveToStorage(userId);
        setupVinculacion(userId);

        if (window.location.href.includes("user_id=")) {
          window.history.replaceState({}, "", window.location.pathname);
        }

        return mappedUser;
      }

      throw new Error(response.message || "Usuario no válido");
    } catch (err) {
      return null;
    }
  }, [saveToStorage]);

  // ---------------- COMPANIES ----------------
  const loadCompanies = useCallback(async (userId) => {
    try {
      const res = await companiesApi.list({ user_id: userId });
      const items = res.item || res.data || res.items || [];
      if ((res.success || res.status === "success") && Array.isArray(items)) {
        setCompanies(items);
        return items;
      }

      return [];
    } catch {
      return [];
    }
  }, []);

  // ---------------- ACCOUNTS ----------------
  const loadAccounts = useCallback(async (companyId) => {
    if (!companyId) return [];

    try {
      const res = await accountsApi.listByCompany({ company_id: companyId });

      if (res.success && Array.isArray(res.item)) {
        setAccounts(res.item);
        return res.item;
      }

      return [];
    } catch {
      return [];
    }
  }, []);

  // ---------------- SWITCH COMPANY (Movido arriba de Profiles) ----------------
  const switchCompany = useCallback(async (companyId) => {
    if (!companyId) {
      return null;
    }

    setIsContextLoading(true);

    try {
      const res = await companiesApi.get({ id: companyId });

      const company = res.item || res.data;
      if ((res.success || res.status === "success") && company) {
        // Normalizar: asegurar que el objeto tenga el campo 'id'
        if (company.id == null) {
          company.id = company.company_id || company.id_empresa || company.empresa_id || company.ID || companyId;
        }
        setActiveCompany(company);
        setActiveAccount(null);

        saveToStorage(user?.id, companyId, null);

        await loadAccounts(companyId);

        window.dispatchEvent(new CustomEvent("crm:contextChanged"));
        return company;
      }

      return null;
    } catch {
      return null;
    } finally {
      setIsContextLoading(false);
    }
  }, [loadAccounts, saveToStorage, user]);

  // ---------------- SWITCH ACCOUNT ----------------
  const switchAccount = useCallback(async (accountId) => {
    if (!accountId) return;

    setIsContextLoading(true);

    try {
      const res = await accountsApi.get({ account_id: accountId });

      if (res.success && res.item) {
        setActiveAccount(res.item);
        saveToStorage(user?.id, activeCompany?.id, accountId);

        window.dispatchEvent(new CustomEvent("crm:contextChanged"));
      }
    } finally {
      setIsContextLoading(false);
    }
  }, [activeCompany, saveToStorage, user]);

  // ---------------- PROFILES ----------------
  const loadProfiles = useCallback(async (userSyncId) => {
    try {
      const res = await profilesApi.list({ user_id: userSyncId });
      if (res.success && Array.isArray(res.items)) {
        setProfiles(res.items);

        const storedProfileId = localStorage.getItem(STORAGE_KEYS.PROFILE_ID);
        const savedProfile = storedProfileId
          ? res.items.find(p => p.id === parseInt(storedProfileId, 10))
          : null;
        const defaultProfile = savedProfile || res.items.find(p => p.is_default) || res.items[0];

        if (defaultProfile) {
          setActiveProfile(defaultProfile);
          if (!savedProfile) {
            localStorage.setItem(STORAGE_KEYS.PROFILE_ID, defaultProfile.id.toString());
          }
        }

        return res.items;
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  const switchProfile = useCallback(async (profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    if (!profile) return;

    // Limpiar datos obsoletos ANTES de cambiar el estado (evita flicker con datos del perfil anterior)
    window.dispatchEvent(new CustomEvent("crm:profileChanging"));

    setActiveProfile(profile);
    localStorage.setItem(STORAGE_KEYS.PROFILE_ID, profileId.toString());
    if (profile.profile_type) {
      localStorage.setItem(STORAGE_KEYS.PROFILE_TYPE, profile.profile_type);
    }

    if (profile.profile_type === 'empresa') {
      await switchCompany(profile.id);
    } else {
      // Para perfiles main/professional: sin empresa activa
      setActiveCompany(null);
      setActiveAccount(null);
    }

    // Esperar a que React flushee state updates y los useEffects actualicen los refs
    // antes de notificar a los contexts
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("crm:profileChanged", { detail: { profile } }));
    }, 0);
  }, [profiles, switchCompany]);

  // ---------------- INIT ----------------
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      const { userId: urlUserId, token: urlToken, profileId: urlProfileId, profileType: urlProfileType } = getAuthDataFromUrl();
      const stored = getStoredContext();

      let userId = urlUserId || stored.userId;
      let jwtUser = null;

      // Si el holding nos envía un profile_id y profile_type en la URL, los usamos como activos
      if (urlProfileId) {
        localStorage.setItem(STORAGE_KEYS.PROFILE_ID, urlProfileId);
      }
      if (urlProfileType) {
        localStorage.setItem(STORAGE_KEYS.PROFILE_TYPE, urlProfileType);
      }

      if (urlToken) {
        const decoded = jwtDecode(urlToken);
        if (decoded) {
          localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, urlToken);
          const userIdFromToken = decoded.user_id || decoded.id;
          const mappedUser = {
            id: userIdFromToken,
            external_user_id: userIdFromToken,
            email: decoded.recovery_email || decoded.email || decoded.correo,
            nombre: decoded.name || decoded.nombre,
            apellido: decoded.apellido || '',
            avatar: decoded.avatar || null,
            role: decoded.role || (decoded.id_tipo === 1 ? 'ADMIN' : 'USER'),
          };

          jwtUser = mappedUser;
          setUser(mappedUser);
          setIsAuthenticated(true);
          userId = userIdFromToken;

          setupVinculacion(userIdFromToken);

          const params = new URLSearchParams(window.location.search);
          params.delete("token"); params.delete("jwt");
          params.delete("userId"); params.delete("user_id");
          params.delete("profile_id"); params.delete("profile_type");
          window.history.replaceState({}, "", window.location.pathname + (params.toString() ? "?" + params.toString() : ""));
        }
      }

      if (!userId || (!urlUserId && !urlToken && isSessionExpired())) {
        clearStorage();
        setIsLoading(false);
        return;
      }

      const apiUser = await loadUserData(userId);
      let u = apiUser || jwtUser;
      if (!u) {
        clearStorage();
        setIsLoading(false);
        return;
      }

      let defaultProfileForCompany = null;
      if (u.id) {
        const profilesList = await loadProfiles(u.id);
        const storedProfileId = localStorage.getItem(STORAGE_KEYS.PROFILE_ID);
        defaultProfileForCompany = storedProfileId
          ? profilesList.find(p => p.id === parseInt(storedProfileId, 10))
          : profilesList.find(p => p.is_default) || profilesList[0];
      }

      // Solo cargar empresas si el perfil es 'empresa'. Para 'main', no hay empresa activa.
      if (defaultProfileForCompany?.profile_type === 'empresa') {
        const companiesList = await loadCompanies(userId);

        if (companiesList.length) {
          const companyId = defaultProfileForCompany.id || companiesList[0].id;
          const company = await switchCompany(companyId);

          if (company) {
            const accountsList = await loadAccounts(companyId);
            if (accountsList.length) {
              const accountId = stored.accountId || accountsList[0].id;
              await switchAccount(accountId);
            }
          }
        } else {
          const company = await switchCompany(defaultProfileForCompany.id);
          if (company) {
            const companyId = company.id || defaultProfileForCompany.id;
            await loadAccounts(companyId);
          }
        }
      } else {
        // Perfil 'main': sin empresa, sin cuenta
        setActiveCompany(null);
        setActiveAccount(null);
      }

      // Guardar profile en localStorage para que apiCall los lea correctamente
        if (defaultProfileForCompany) {
          localStorage.setItem(STORAGE_KEYS.PROFILE_ID, defaultProfileForCompany.id.toString());
          if (defaultProfileForCompany.profile_type) {
            localStorage.setItem(STORAGE_KEYS.PROFILE_TYPE, defaultProfileForCompany.profile_type);
          }

          setTimeout(() => {
          window.dispatchEvent(new CustomEvent("crm:profileChanged", { detail: { profile: defaultProfileForCompany } }));
        }, 0);
      }

      setIsLoading(false);
    };

    init();
  }, [
    loadUserData,
    loadCompanies,
    loadAccounts,
    switchCompany,
    switchAccount,
    loadProfiles,
    getStoredContext,
    isSessionExpired,
    clearStorage
  ]);

  // ---------------- REFRESH ----------------
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const i = setInterval(() => {
      const stored = getStoredContext();
      if (stored.userId) loadUserData(stored.userId);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(i);
  }, [isAuthenticated, loadUserData, getStoredContext]);

  const logout = () => {
    clearStorage();
    setUser(null);
    setIsAuthenticated(false);
    setActiveCompany(null);
    setActiveAccount(null);
    setActiveProfile(null);
    setProfiles([]);
  };

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    activeCompany,
    activeAccount,
    companies,
    accounts,
    profiles,
    activeProfile,
    switchCompany,
    switchAccount,
    switchProfile,
    logout,
  }), [user, isAuthenticated, isLoading, activeCompany, activeAccount, companies, accounts, profiles, activeProfile, switchCompany, switchAccount, switchProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
